import { useMemo } from 'react';

import { Box, Card, CardProps, Flex, Text } from '@mantine/core';

import { ImageMantine } from '../../../../DataDisplay';
import { cx } from '../../../../Provider/Provider';
import classes from './SelectableCard.module.css';

type SelectableCardProps = {
  stretchImage?: boolean;
  /* this image src is the default provided to the ImageMantine component and should generally be suited for a 2:1 aspect ratio. 
  If not provided, fallbackSrc: DEFAULT_IMAGE_PLACEHOLDER is set on the ImageMantine component */
  imageSrc?: string;
  /* if thumnailSrc is provided, when isColumn is false, it will be used instead of imageSrc should generally be suited for a 1:1 aspect ratio 
  This is used for campaign cards where it is possible to have both a thumbnail and a page image*/
  thumbnailSrc?: string;
  /* this options allows for a different header component, for example, the CalendarDate component used in EventDateCard for CalendarSection.tsx 
  If a header is present, the imageSrc and thumbnailSrc do not need to be provided  and `stretchImage` will be ignored.*/
  header?: React.ReactNode;
  title: string;
  /* this is used to set the number of lines the title can be clamped to, 2 by default */
  titleClamp?: number;
  description?: React.ReactNode;
  detail?: React.ReactNode;
  /* this is used to set flex-direction as column on .SelectableCard__content */
  isColumn?: boolean;
  /* if onClick is provided, we set the Card component to button and add hover and focus styles */
  onClick?: () => void;
  className?: string;
};

const SelectableCard = ({
  imageSrc,
  stretchImage = true,
  thumbnailSrc,
  header,
  title,
  titleClamp = 2,
  description,
  detail,
  isColumn = false,
  onClick,
  className,
  ...rest
}: SelectableCardProps & CardProps) => {
  const _imageSrc = useMemo(() => {
    if (isColumn) {
      return imageSrc;
    }

    if (thumbnailSrc) {
      return thumbnailSrc;
    }

    return imageSrc;
  }, [isColumn, imageSrc, thumbnailSrc]);

  return (
    <Card
      className={cx(
        classes.SelectableCard,
        {
          [classes['SelectableCard--column']]: isColumn,
          [classes['SelectableCard--onClick']]: !!onClick,
        },
        className
      )}
      onClick={onClick}
      component={onClick ? 'button' : 'div'}
      {...rest}
    >
      <Flex className={classes.SelectableCard__content}>
        {header ?? (
          <Box
            className={cx(classes.SelectableCard__imageContainer, {
              [classes['SelectableCard__imageContainer--stretch']]: stretchImage,
            })}
          >
            <ImageMantine src={_imageSrc} radius="sm" className={classes.SelectableCard__image} />
          </Box>
        )}

        <Box className={classes.SelectableCard__body}>
          <Box className={classes.SelectableCard__info}>
            <Text lineClamp={titleClamp} className={classes.SelectableCard__title}>
              {title}
            </Text>
            {!!description && (
              <Box className={classes.SelectableCard__description}>{description}</Box>
            )}
          </Box>
          {!!detail && <Box className={classes.SelectableCard__detail}>{detail}</Box>}
        </Box>
      </Flex>
    </Card>
  );
};

export { SelectableCard };
